import React, { useEffect, useState } from 'react';
import { vimsService } from '../../services/vimsService';
import { recallService } from '../../services/recallService';
import { vehicleService } from '../../services/vehicleService';

/**
 * VIMS 리콜 불러오기 창 (2026-10-30)
 *
 * 새 리콜 등록 화면에서 연다. VIMS 리콜 목록 → 하나를 고르면 리콜 기초 데이터 입력칸을 채워 보여 주고 →
 * 확인하면 기초 데이터(E2B_RECALL_BASE)로 저장해 등록 화면에 바로 넘긴다.
 *
 * - 차종: VIMS 는 프로젝트 차종(예: "포톤 툰랜드 MH"), CRM 은 차량 마스터의 모델명이다. 새 리콜의 대상 차량은
 *   CRM 차량의 modelName 이 **정확히 같은 것**만 잡히므로(pages/Recall.js) 반드시 CRM 차종 목록에서 고르게 한다.
 *   비슷한 이름이 있으면 미리 골라 두되, 사람이 확인한다.
 * - 같은 리콜을 두 번 가져오지 않는다: 기초 데이터에 source(VIMS 프로젝트·리콜 id)를 남기고, 이미 가져온 것은
 *   새로 만들지 않고 그 기초 데이터를 쓴다.
 */

const norm = (s) => String(s || '').toLowerCase().replace(/[\s\-_()[\]·.,/]/g, '');

/**
 * VIMS 차종 이름에 가장 가까운 CRM 차종을 고른다(확신이 없으면 '').
 *
 * 잘못 고르면 다른 차종 차량이 리콜 대상으로 잡히므로 **보수적으로** 고른다 — 못 고르면 사람이 고르면 되지만,
 * 엉뚱하게 미리 골라 두면 그대로 저장될 수 있다.
 * - 브랜드 이름 하나만 겹치는 것("Foton View C2" ↔ "Foton Aumark")은 인정하지 않는다
 * - 후보가 둘 이상 동점이면(VIMS "툰랜드" ↔ CRM "툰랜드 MH"·"툰랜드 G9") 고르지 않는다
 * @param {string} carName VIMS 차종
 * @param {Array<{modelName: string}>} models CRM 차종 목록
 * @returns {string} CRM 모델명 또는 ''
 */
export function guessModel(carName, models) {
  const c = norm(carName);
  if (!c) return '';
  let best = '';
  let bestScore = 0;
  let tie = false;
  for (const m of models || []) {
    const name = String((m && m.modelName) || '');
    const n = norm(name);
    if (!n) continue;
    let score = 0;
    if (n === c) score = 1000;
    else if (c.includes(n) || n.includes(c)) score = Math.min(n.length, c.length) * 10;
    else {
      // 띄어쓰기 단위로 겹치는 낱말(두 글자 이상) — CRM 모델명의 60% 이상이 겹칠 때만 인정한다.
      const words = String(carName).split(/\s+/).map(norm).filter((w) => w.length >= 2);
      const covered = words.filter((w) => n.includes(w)).reduce((sum, w) => sum + w.length, 0);
      score = covered / n.length >= 0.6 ? covered : 0;
    }
    if (score > bestScore) { bestScore = score; best = name; tie = false; }
    else if (score > 0 && score === bestScore) tie = true;
  }
  return tie ? '' : best;
}

const sameSource = (base, item) => base && base.source && base.source.system === 'VIMS'
  && base.source.projectId === item.projectId && base.source.recallId === item.recallId;

export default function VimsRecallImport({ existingBases = [], onClose, onImported }) {
  const [phase, setPhase] = useState('loading'); // loading | list | form | saving | error
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [models, setModels] = useState([]);
  const [picked, setPicked] = useState(null);
  const [form, setForm] = useState(null);

  const load = async () => {
    setPhase('loading');
    setError('');
    try {
      const [list, m] = await Promise.all([vimsService.fetchRecalls(), vehicleService.getVehicleModels()]);
      setItems(list || []);
      setModels(m || []);
      setPhase('list');
    } catch (e) {
      setError(e.message || 'VIMS 리콜을 불러오지 못했습니다.');
      setPhase('error');
    }
  };

  // 창이 열릴 때 한 번만 가져온다.
  useEffect(() => { load(); }, []);

  const pick = (item) => {
    const existing = existingBases.find((b) => sameSource(b, item));
    if (existing) { onImported(existing); return; }
    const summary = [item.range, item.total ? `대상 ${item.total.toLocaleString()}대` : ''].filter(Boolean).join(' · ');
    setPicked(item);
    setForm({
      recallType: '자진리콜',
      title: item.title,
      modelName: guessModel(item.carName, models),
      startDate: item.startDate,
      endDate: item.endDate,
      reason: summary,
      standardLabor: '',
      partsRequired: '',
      memo: `VIMS에서 가져옴 — 프로젝트 차종: ${item.carName || '-'}`
    });
    setPhase('form');
  };

  const save = async () => {
    if (!form.title.trim()) return alert('캠페인 명칭을 입력해 주세요.');
    if (!form.modelName) return alert('대상 차량 모델을 CRM 차종 목록에서 골라 주세요.\n(대상 차량은 이 모델명이 같은 차량으로 잡힙니다.)');
    setPhase('saving');
    try {
      const base = {
        ...form,
        title: form.title.trim(),
        source: {
          system: 'VIMS',
          projectId: picked.projectId,
          recallId: picked.recallId,
          carName: picked.carName,
          title: picked.title,
          importedAt: new Date().toISOString()
        }
      };
      const id = await recallService.addRecallBase(base);
      onImported({ ...base, id });
    } catch (e) {
      alert('기초 데이터를 저장하지 못했습니다: ' + (e.message || e));
      setPhase('form');
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.head}>
          <h3 style={{ margin: 0, fontSize: '18px' }}>📥 VIMS에서 리콜 불러오기</h3>
          <button type="button" style={S.close} onClick={onClose} aria-label="닫기">✕</button>
        </div>

        {phase === 'loading' && (
          <div style={S.center}>VIMS에서 리콜 목록을 가져오는 중… <span style={S.muted}>(처음에는 10초쯤 걸릴 수 있습니다)</span></div>
        )}

        {phase === 'error' && (
          <div style={S.center}>
            <div style={{ color: '#dc2626', fontWeight: 700, marginBottom: '14px' }}>{error}</div>
            <button type="button" style={S.btnGhost} onClick={load}>다시 시도</button>
          </div>
        )}

        {phase === 'list' && (
          items.length === 0 ? (
            <div style={S.center}>VIMS에 등록된 리콜이 없습니다. <span style={S.muted}>(VIMS → 15. 리콜/결함관리)</span></div>
          ) : (
            <>
              <p style={S.lead}>가져올 리콜을 고르세요. 이미 가져온 리콜은 새로 만들지 않고 그 기초 데이터를 씁니다.</p>
              <div style={S.listBox}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>VIMS 차종</th><th style={S.th}>리콜 명칭</th><th style={S.th}>시행 기간</th>
                      <th style={S.th}>대상 범위</th><th style={{ ...S.th, textAlign: 'right' }}>대상 수</th><th style={S.th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => {
                      const done = existingBases.some((b) => sameSource(b, it));
                      return (
                        <tr key={it.projectId + '/' + it.recallId}>
                          <td style={S.td}>{it.carName || '-'}</td>
                          <td style={{ ...S.td, fontWeight: 700 }}>{it.title}</td>
                          <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{it.startDate || '-'} ~ {it.endDate || '-'}</td>
                          <td style={S.td}>{it.range || '-'}</td>
                          <td style={{ ...S.td, textAlign: 'right' }}>{it.total ? it.total.toLocaleString() : '-'}</td>
                          <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {done && <span style={S.badge}>가져옴</span>}
                            <button type="button" style={done ? S.btnGhostSm : S.btnPickSm} onClick={() => pick(it)}>
                              {done ? '이것 쓰기' : '고르기'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )
        )}

        {(phase === 'form' || phase === 'saving') && form && (
          <>
            <div style={S.source}>
              VIMS 원본 — 차종 <b>{picked.carName || '-'}</b> · 리콜 <b>{picked.title}</b>
              {picked.range ? ` · ${picked.range}` : ''}{picked.total ? ` · 대상 ${picked.total.toLocaleString()}대` : ''}
            </div>

            <div style={S.grid2}>
              <div><label style={S.label}>리콜 구분</label>
                <select style={S.input} value={form.recallType} onChange={set('recallType')}>
                  <option value="자진리콜">📢 자진리콜</option><option value="국토부리콜">🛑 국토부리콜</option>
                </select>
              </div>
              <div><label style={S.label}>리콜 캠페인 명칭</label><input style={S.input} value={form.title} onChange={set('title')} /></div>
            </div>

            <div style={S.grid3}>
              <div>
                <label style={S.label}>대상 차량 모델 (CRM 차종)</label>
                <select style={{ ...S.input, borderColor: form.modelName ? '#cbd5e1' : '#f59e0b' }} value={form.modelName} onChange={set('modelName')}>
                  <option value="">-- CRM 차종에서 고르세요 --</option>
                  {models.map((m) => <option key={m.id} value={m.modelName}>{m.modelName}</option>)}
                </select>
                <div style={S.hint}>
                  {form.modelName
                    ? `VIMS "${picked.carName || '-'}" 와 비슷해 미리 골랐습니다. 맞는지 확인하세요.`
                    : `VIMS "${picked.carName || '-'}" 와 맞는 CRM 차종을 찾지 못했습니다. 직접 고르세요.`}
                </div>
              </div>
              <div><label style={S.label}>시행 시작일</label><input type="date" style={S.input} value={form.startDate} onChange={set('startDate')} /></div>
              <div><label style={S.label}>시행 종료일</label><input type="date" style={S.input} value={form.endDate} onChange={set('endDate')} /></div>
            </div>

            <div style={S.grid21}>
              <div><label style={S.label}>결함 내용 요약</label><input style={S.input} value={form.reason} onChange={set('reason')} placeholder="VIMS에는 결함 내용이 없어 대상 범위를 넣어 두었습니다" /></div>
              <div><label style={S.label}>표준 공임 (Hour)</label><input type="number" style={S.input} value={form.standardLabor} onChange={set('standardLabor')} /></div>
            </div>

            <div style={{ marginBottom: '22px' }}>
              <label style={S.label}>필요 부품 및 자재</label>
              <input style={S.input} value={form.partsRequired} onChange={set('partsRequired')} placeholder="VIMS에 없는 항목입니다 — 직접 입력" />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" style={S.btnGhost} onClick={() => setPhase('list')} disabled={phase === 'saving'}>← 목록</button>
              <button type="button" style={S.btnPrimary} onClick={save} disabled={phase === 'saving'}>
                {phase === 'saving' ? '저장 중…' : '기초 데이터로 저장하고 채우기'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modal: { background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '960px', maxHeight: '86vh', overflowY: 'auto', padding: '24px 28px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  close: { background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#64748b' },
  center: { padding: '40px 10px', textAlign: 'center', color: '#334155', fontSize: '14px' },
  muted: { color: '#94a3b8', fontSize: '12px' },
  lead: { margin: '0 0 12px', fontSize: '13px', color: '#475569' },
  listBox: { border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { textAlign: 'left', padding: '10px 12px', background: '#f8fafc', color: '#64748b', fontWeight: 700, borderBottom: '1px solid #e2e8f0' },
  td: { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', color: '#1e293b', verticalAlign: 'middle' },
  badge: { fontSize: '11px', fontWeight: 700, color: '#047857', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '999px', padding: '2px 8px', marginRight: '8px' },
  source: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px 14px', fontSize: '13px', color: '#475569', marginBottom: '18px' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '16px', marginBottom: '16px' },
  grid3: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px', marginBottom: '16px' },
  grid21: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '16px' },
  label: { display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '6px' },
  input: { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', background: '#fff' },
  hint: { fontSize: '11.5px', color: '#64748b', marginTop: '5px', lineHeight: 1.45 },
  btnPrimary: { padding: '10px 18px', background: '#1e293b', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' },
  btnGhost: { padding: '10px 16px', background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' },
  btnPickSm: { padding: '6px 12px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 700, cursor: 'pointer', fontSize: '12px' },
  btnGhostSm: { padding: '6px 12px', background: '#fff', color: '#334155', border: '1px solid #cbd5e1', borderRadius: '6px', fontWeight: 700, cursor: 'pointer', fontSize: '12px' }
};
